import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { CompanyDto } from '@/services/ws-companies/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNotification } from '@/js/libs/ui/src/V2';

import useLocalStorage, { StorageKey as LocalStorageKey } from 'src/hooks/useLocalStorage';
import { useCleanupSessionStorageItems } from './useCleanupSessionStorageItems';

export type CompanyIdentifiersFragment = Pick<CompanyDto, 'id' | 'companyUUID' | 'companyRole' | 'name'>;

const { useCompanySwitchContext } = createContexts({
  CompanySwitch() {
    const { saveJSON, removeItem, loadJSON } = useLocalStorage();
    const cleanupSessionStorageItems = useCleanupSessionStorageItems();

    const [t] = useTranslation(['backend', 'common']);
    const notify = useNotification();

    const [, setSearchParams] = useSearchParams();

    const [company, setCompany] = useState<CompanyIdentifiersFragment | null>(() =>
      loadJSON(LocalStorageKey.COMPANY, (e) => {
        // Defer notification to after render completes
        queueMicrotask(() => {
          notify.error({
            body: t((e as Error).message),
          });
        });
        // Also clear the corrupted data to prevent future issues
        removeItem(LocalStorageKey.COMPANY);
      }),
    );

    const handleCompanyChange = useCallbackRef((option: CompanyIdentifiersFragment | null) => {
      if (option) {
        setCompany(option);
        try {
          saveJSON(LocalStorageKey.COMPANY, {
            id: option.id,
            companyUUID: option.companyUUID,
            companyRole: option.companyRole,
            name: option.name,
          });
        } catch (e) {
          notify.error({ body: t((e as Error).message) });
        }
      } else {
        setCompany(null);
        removeItem(LocalStorageKey.COMPANY);
      }

      // Let listeners finish their job
      setTimeout(() => {
        setSearchParams(undefined);
        cleanupSessionStorageItems();
      }, 0);
    });

    return {
      company,
      onCompanyChange: handleCompanyChange,
    };
  },
});

const CompanySwitchProvider = useCompanySwitchContext.Provider;

export { useCompanySwitchContext, CompanySwitchProvider };
